/**
 * Event reminder runtime — storage, metronome, refresh and native hand-off.
 *
 * The arithmetic lives next door in `event-reminders-plan.ts` (pure, bundlable,
 * testable). This module is the stateful half: it decides WHEN to re-read the
 * calendar, keeps the resulting plan in `localStorage`, ticks a clock over it,
 * pushes what is due onto {@link dueReminders} for `ReminderHost.vue`, and mirrors
 * the future ones onto the device so a closed app still reminds.
 *
 * Reminders reach the user through three independent channels, by design:
 *
 *   1. the ERPat bell notification, sent by a server cron — the only one that
 *      works when the app has never been opened;
 *   2. the in-app banner, driven by the tick below, for a user who is here;
 *   3. a scheduled Android notification, for a user who is not.
 *
 * They agree because they share the audience rule (`shouldRemind`) and the
 * all-day anchor (`REMINDER_ALL_DAY_ANCHOR_MIN`, twin of the server constant).
 * There is NO web push and no FCM — on the web, channel 3 does not exist.
 *
 * Two invariants hold the whole thing together:
 *
 *   - **A reminder is marked fired and PERSISTED before it is shown.** Same
 *     loop-guard rule as `pwa/version-check.ts`: a render error, a reload, or a
 *     second tick arriving mid-render must never be able to re-raise the same
 *     reminder.
 *   - **The registry is written before native is touched.** If the app dies
 *     between the two, the next refresh reconciles the device against a plan
 *     that is already on disk; the reverse order could strand a notification
 *     nothing remembers scheduling.
 *
 * Lifecycle is driven by the session store: started when a token pair is issued
 * or restored, stopped on sign-out, and CLEARED (storage + device) at sign-out
 * while the token can still name the user whose key to remove.
 */
import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { eventsService } from '@/services/erpat';
import {
  getSessionEpoch,
  getSessionUserId,
  getStoredCompanyKey,
  hasAccessToken,
} from '@/services/api/erpat-client';
import { whenEventsAllowed } from '@/services/events-access';
import { useSettingsStore } from '@/stores/settings.store';
import * as localNotifications from '@/services/device/local-notifications.service';
import {
  dueNow,
  missed,
  planReminders,
  reminderBody,
  type ReminderEntry,
} from '@/services/event-reminders-plan';
import type { EventVM, ReminderLead } from '@/types/event';

/** Tick cadence. Twin of DEFAULT_INTERVAL_MS in reminder-ticker.worker.ts. */
const REMINDER_TICK_MS = 30 * 1000;

/**
 * Floor between calendar refreshes. The tick is cheap (it reads storage); the
 * refresh is a network round trip, so it is rationed — a user switching tabs
 * every few seconds must not turn into a request per switch.
 */
const REFRESH_MIN_GAP_MS = 15 * 60 * 1000;

/**
 * How far ahead the plan looks, in days. Two days covers the longest lead
 * ("1 day before") with a full day of slack, so a reminder is always armed well
 * before it is needed without pulling a month of calendar on every refresh.
 */
const HORIZON_DAYS = 2;

/** Page size and page cap for the horizon fetch (the API's per_page max is 100). */
const PER_PAGE = 100;
const MAX_PAGES = 3;

/** Hard cap on stored entries — a runaway recurring series can't eat storage. */
const MAX_ENTRIES = 200;

/** Entries whose event started longer ago than this are dropped on write. */
const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

const STORAGE_PREFIX = 'syntry_event_reminders:';

/** What one user's plan looks like on disk. */
interface ReminderRegistry {
  /** Schema version — a bump makes an old payload read as empty, not as junk. */
  version: 1;
  /** The lead the plan was built for; a change invalidates every key. */
  lead: ReminderLead;
  /** Epoch ms of the refresh that produced it. */
  refreshedAt: number;
  entries: Record<string, ReminderEntry>;
}

/**
 * Reminders waiting to be shown, oldest first. `ReminderHost.vue` renders
 * `[0]` and shifts the queue on dismiss.
 */
export const dueReminders: Ref<ReminderEntry[]> = ref([]);

/** The reminder currently on screen, if any. */
export const nextReminder: ComputedRef<ReminderEntry | null> = computed(
  () => dueReminders.value[0] ?? null,
);

let running = false;
let worker: Worker | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let refreshInFlight = false;
let replanQueued = false;
let lastRefreshAt = 0;

/* ── Storage ─────────────────────────────────────────────────────────────── */

/**
 * Partitioned by tenant AND user, exactly like `todo-local-fields.ts`.
 *
 * Event ids are small integers that repeat across tenants and users, so a
 * single global key would let one person's event 42 inherit another's plan —
 * and, worse, put another company's event TITLE on this device's lock screen.
 */
function storageKey(): string {
  const tenant = (getStoredCompanyKey() ?? '').trim().toLowerCase();
  return `${STORAGE_PREFIX}${tenant}:${getSessionUserId()}`;
}

function emptyRegistry(lead: ReminderLead): ReminderRegistry {
  return { version: 1, lead, refreshedAt: 0, entries: {} };
}

/** Narrow one stored record back to a ReminderEntry, or null if it is junk. */
function toEntry(value: unknown): ReminderEntry | null {
  if (typeof value !== 'object' || value === null) return null;
  const row = value as Record<string, unknown>;
  if (typeof row.key !== 'string' || row.key === '') return null;
  if (typeof row.startAt !== 'number' || !Number.isFinite(row.startAt)) return null;
  if (typeof row.fireAt !== 'number' || !Number.isFinite(row.fireAt)) return null;
  return {
    key: row.key,
    eventId: typeof row.eventId === 'string' ? row.eventId : '',
    occurrenceDate: typeof row.occurrenceDate === 'string' ? row.occurrenceDate : '',
    title: typeof row.title === 'string' ? row.title : '',
    startAt: row.startAt,
    fireAt: row.fireAt,
    fired: row.fired === true,
    nativeId: typeof row.nativeId === 'number' ? row.nativeId : 0,
  };
}

/**
 * Read the plan. Never throws and never returns a partial shape — private mode,
 * a cleared key, a half-written value and a schema bump all read as "empty",
 * which simply means the next refresh rebuilds from scratch.
 */
function readRegistry(): ReminderRegistry {
  const lead = currentLead();
  if (typeof window === 'undefined') return emptyRegistry(lead);
  try {
    const raw = window.localStorage.getItem(storageKey());
    if (!raw) return emptyRegistry(lead);
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return emptyRegistry(lead);
    const record = parsed as Record<string, unknown>;
    if (record.version !== 1) return emptyRegistry(lead);

    const entries: Record<string, ReminderEntry> = {};
    const stored = record.entries;
    if (typeof stored === 'object' && stored !== null) {
      for (const [key, value] of Object.entries(stored as Record<string, unknown>)) {
        const entry = toEntry(value);
        if (entry) entries[key] = entry;
      }
    }
    return {
      version: 1,
      lead: typeof record.lead === 'string' ? (record.lead as ReminderLead) : lead,
      refreshedAt: typeof record.refreshedAt === 'number' ? record.refreshedAt : 0,
      entries,
    };
  } catch {
    return emptyRegistry(lead);
  }
}

/**
 * Drop what is no longer worth keeping, then cap.
 *
 * Soonest-first so the cap sheds the far end of the horizon rather than the
 * reminder about to fire.
 */
function prune(entries: Record<string, ReminderEntry>, now: number): Record<string, ReminderEntry> {
  const live = Object.values(entries)
    .filter((entry) => now - entry.startAt < STALE_AFTER_MS)
    .sort((a, b) => a.fireAt - b.fireAt)
    .slice(0, MAX_ENTRIES);

  const out: Record<string, ReminderEntry> = {};
  for (const entry of live) out[entry.key] = entry;
  return out;
}

function writeRegistry(registry: ReminderRegistry): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(), JSON.stringify(registry));
  } catch {
    /* private mode / quota — the plan just doesn't survive this page session */
  }
}

/* ── The lead preference ─────────────────────────────────────────────────── */

/**
 * The user's lead, from the settings store.
 *
 * Guarded because this is reachable from a metronome tick: if Pinia is not
 * active (a teardown race), fall back to the documented default rather than
 * throwing out of a timer callback where nothing can catch it.
 */
function currentLead(): ReminderLead {
  try {
    return useSettingsStore().eventReminderLead;
  } catch {
    return '1d';
  }
}

/**
 * Take the server's copy of the preference, which is the source of truth (the
 * bell notifications are scheduled from it). Best-effort: offline, or an
 * endpoint the tenant has not migrated yet, leaves the local value alone.
 */
async function adoptServerPreference(): Promise<void> {
  if (!hasAccessToken()) return;
  // Capture the epoch BEFORE awaiting: a preference resolving after sign-out
  // must not write into the next session's settings.
  const epoch = getSessionEpoch();
  try {
    const lead = await eventsService.reminderPreference.get();
    if (epoch !== getSessionEpoch() || !running) return;
    const store = useSettingsStore();
    if (store.eventReminderLead === lead) return;
    store.adoptServerEventReminderLead(lead);
    rescheduleEventReminders();
  } catch {
    /* keep the local copy — see the docblock */
  }
}

/* ── Refreshing the plan ─────────────────────────────────────────────────── */

/** `YYYY-MM-DD` in the device's own timezone — the API's date bounds are civil. */
function dateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Pull every event inside the horizon.
 *
 * Paging is driven by `pagination.total`, NOT by the size of the page: `total`
 * counts BASE rows, and `include_recurring` expands a series server-side, so a
 * page can legitimately hold more entries than `per_page` — which makes the
 * usual "a short page means the end" test wrong here.
 */
async function fetchHorizon(): Promise<EventVM[]> {
  const from = new Date();
  const to = new Date(from.getTime());
  to.setDate(to.getDate() + HORIZON_DAYS);

  const events: EventVM[] = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const result = await eventsService.list({
      start_date: dateKey(from),
      end_date: dateKey(to),
      include_recurring: true,
      order: 'asc',
      page,
      per_page: PER_PAGE,
    });
    events.push(...result.items);
    if (result.items.length === 0) break;
    const total = result.pagination.total;
    if (!Number.isFinite(total) || total <= 0) break;
    if (page * PER_PAGE >= total) break;
  }
  return events;
}

/**
 * Bring the device's scheduled notifications in line with the plan.
 *
 * Runs only when a native shell has actually granted permission — this is never
 * the place that prompts. Cancels anything that left the plan or has already
 * fired in-app, then (re)schedules the future ones; ids are deterministic, so
 * re-scheduling replaces rather than duplicates.
 */
async function reconcileNative(
  previous: Readonly<Record<string, ReminderEntry>>,
  next: Readonly<Record<string, ReminderEntry>>,
  lead: ReminderLead,
): Promise<void> {
  if (!localNotifications.isSupported()) return;
  if ((await localNotifications.checkPermission()) !== 'granted') return;

  const cancelIds: number[] = [];
  for (const [key, entry] of Object.entries(previous)) {
    if (!(key in next) && entry.nativeId) cancelIds.push(entry.nativeId);
  }
  for (const entry of Object.values(next)) {
    // Already surfaced in-app — the device copy would be a duplicate.
    if (entry.fired && entry.nativeId) cancelIds.push(entry.nativeId);
  }
  await localNotifications.cancel(cancelIds);

  await localNotifications.ensureChannel();

  const now = Date.now();
  const items = Object.values(next)
    .filter((entry) => !entry.fired && entry.fireAt > now && entry.nativeId > 0)
    .map((entry) => ({
      id: entry.nativeId,
      title: entry.title || 'Upcoming event',
      // Phrased as of the FIRE time, not now — the text is read on the lock
      // screen when it arrives, so "starts in 20 minutes" must be true then.
      body: reminderBody(entry, lead, entry.fireAt),
      at: new Date(entry.fireAt),
      eventId: entry.eventId,
      occurrenceDate: entry.occurrenceDate,
    }));
  await localNotifications.schedule(items);
}

/** Nothing to remind about: store an empty plan and cancel what the device armed. */
async function disarm(lead: ReminderLead, at: number): Promise<void> {
  writeRegistry({ version: 1, lead, refreshedAt: at, entries: {} });
  dueReminders.value = [];
  await localNotifications.cancelAllMine();
}

/**
 * Re-read the calendar and rebuild the plan.
 *
 * Gated four ways: there must be a session, the app must be in the foreground
 * (a background refresh would burn battery for a plan that is already armed on
 * the device), only one may run at a time, and — unless forced — the last one
 * must be at least {@link REFRESH_MIN_GAP_MS} old.
 */
async function refreshPlan(force: boolean): Promise<void> {
  if (!hasAccessToken()) return;
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
  if (refreshInFlight) {
    // A reschedule arriving mid-flight would otherwise be swallowed, leaving
    // the plan built for the PREVIOUS lead until the next 15-minute window.
    if (force) replanQueued = true;
    return;
  }

  const startedAt = Date.now();
  if (!force && startedAt - lastRefreshAt < REFRESH_MIN_GAP_MS) return;

  // Stamped before the await, like the version watchdog: a failing backend then
  // backs off a full window instead of retrying on every 30-second tick.
  lastRefreshAt = startedAt;
  refreshInFlight = true;
  const epoch = getSessionEpoch();
  const lead = currentLead();

  try {
    const previous = readRegistry();

    if (lead === 'off') {
      // Nothing to plan. Write the empty plan, disarm the device, and skip the
      // network entirely — "off" must not cost a request every 15 minutes.
      await disarm(lead, startedAt);
      return;
    }

    // `null` when this account cannot list events (no `event` permit, or the
    // module is off): the request is never sent — see services/events-access.ts.
    const events = await whenEventsAllowed(fetchHorizon);
    // Signed out (or switched accounts) mid-flight — this plan belongs to a
    // session that no longer exists, and writing it would key it to whoever is
    // signed in now.
    if (epoch !== getSessionEpoch() || !running) return;

    if (events === null) {
      // No calendar to plan from, so no reminders — the same end state as
      // "off". It also retires any plan left from before a permit was removed.
      await disarm(lead, startedAt);
      return;
    }

    // Re-read AFTER the await and union the `fired` flags with the pre-await
    // snapshot. A tick that ran DURING the fetch marks an entry fired and
    // persists it; rebuilding from the stale `previous` would write `fired:
    // false` back over that, and the tick() at the end of this function would
    // then re-detect the same entry as due and queue a SECOND identical banner
    // — the one the user just dismissed reappears. Merging is what keeps the
    // module's "a reminder is marked fired and PERSISTED before it is shown"
    // invariant true across a refresh.
    const fresh = readRegistry();
    const carried: Record<string, ReminderEntry> = { ...previous.entries, ...fresh.entries };
    for (const [key, entry] of Object.entries(carried)) {
      if (previous.entries[key]?.fired || fresh.entries[key]?.fired) {
        carried[key] = { ...entry, fired: true };
      }
    }

    const now = Date.now();
    const entries = prune(planReminders(events, lead, now, carried), now);

    // Registry FIRST — see the module docblock. `carried` (not `previous`) is
    // also what native reconciles against, so an entry that only appeared on
    // disk mid-fetch still gets its device notification cancelled if the new
    // plan dropped it.
    writeRegistry({ version: 1, lead, refreshedAt: now, entries });
    await reconcileNative(carried, entries, lead);
    tick();
  } catch {
    // Offline, a server error, a transport hiccup — the plan on disk stays valid
    // and the next window tries again. A 403/404 was also remembered by
    // whenEventsAllowed(), so the next window disarms instead of asking again.
  } finally {
    refreshInFlight = false;
    if (replanQueued) {
      replanQueued = false;
      lastRefreshAt = 0;
      void refreshPlan(true);
    }
  }
}

/* ── The tick ────────────────────────────────────────────────────────────── */

/** Refresh opportunistically from a tick, when the window has elapsed. */
function maybeRefresh(): void {
  if (!hasAccessToken()) return;
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
  if (Date.now() - lastRefreshAt < REFRESH_MIN_GAP_MS) return;
  void refreshPlan(false);
}

/**
 * One pass over the stored plan. Makes NO network request — a tick must be
 * cheap enough to run every 30 seconds forever.
 */
function tick(): void {
  const registry = readRegistry();
  const entries = Object.values(registry.entries);
  if (entries.length === 0) {
    maybeRefresh();
    return;
  }

  const now = Date.now();
  // Silence first: an entry that is both stale and nominally "due" must be
  // classified as missed, and `dueNow` already excludes it.
  const silenced = missed(entries, now);
  const due = dueNow(entries, now);

  if (silenced.length > 0 || due.length > 0) {
    for (const entry of [...silenced, ...due]) {
      const stored = registry.entries[entry.key];
      if (stored) stored.fired = true;
    }
    // Persist BEFORE surfacing anything. If the write fails we still show the
    // reminder (fail open — a possible repeat beats a silent miss), but on the
    // happy path this is what makes a reminder strictly once-only.
    writeRegistry(registry);
  }

  if (due.length > 0) {
    // Belt and braces: dedupe by key. The persisted `fired` flag above is what
    // normally makes a reminder once-only, but that write is deliberately
    // fail-open (private mode, quota) and `dueReminders` is a QUEUE the host
    // shifts — so a duplicate would put the identical banner straight back on
    // screen the moment the user dismisses it.
    const queued = new Set(dueReminders.value.map((entry) => entry.key));
    const pending = due.filter((entry) => !queued.has(entry.key));
    if (pending.length > 0) {
      dueReminders.value = [
        ...dueReminders.value,
        ...pending.map((entry) => ({ ...entry, fired: true })),
      ];
    }
  }

  maybeRefresh();
}

/* ── Metronome + wake-ups ────────────────────────────────────────────────── */

/**
 * Start the tick source. Primary: a dedicated Web Worker, whose timers escape
 * background-tab throttling. Fallback: a main-thread interval, when the worker
 * can't be constructed or later fails to load (CSP, packaging).
 */
function startMetronome(): void {
  try {
    worker = new Worker(new URL('./api/reminder-ticker.worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = () => tick();
    worker.onerror = () => {
      // Script failed to load/run — swap to the interval fallback. Fires only
      // while we own a live worker (terminate() silences it), so this cannot
      // revive a stopped metronome.
      stopMetronome();
      timer = setInterval(() => tick(), REMINDER_TICK_MS);
    };
    // Retune the worker to OUR cadence so the two constants can never drift.
    worker.postMessage({ intervalMs: REMINDER_TICK_MS });
  } catch {
    worker = null;
  }
  if (!worker) {
    timer = setInterval(() => tick(), REMINDER_TICK_MS);
  }
}

/** Stop whichever tick source is active. Idempotent. */
function stopMetronome(): void {
  if (worker !== null) {
    worker.terminate();
    worker = null;
  }
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') tick();
}

/**
 * Page Lifecycle `resume` — fires when the browser unfreezes a frozen tab
 * (which can happen without the tab becoming visible, so `visibilitychange`
 * alone misses it). While frozen NOTHING ran, not even the worker.
 */
function onResume(): void {
  tick();
}

/** bfcache restore — a `persisted` pageshow skips normal startup entirely. */
function onPageShow(event: PageTransitionEvent): void {
  if (event.persisted) tick();
}

/** Network returned — a refresh that failed during the outage can retry now. */
function onOnline(): void {
  lastRefreshAt = 0;
  tick();
}

function attachListeners(): void {
  document.addEventListener('visibilitychange', onVisibilityChange);
  // Page Lifecycle 'resume' is not in TS's DocumentEventMap yet — the string
  // overload of addEventListener covers it; unsupported browsers never fire it.
  document.addEventListener('resume', onResume);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('online', onOnline);
}

function detachListeners(): void {
  document.removeEventListener('visibilitychange', onVisibilityChange);
  document.removeEventListener('resume', onResume);
  window.removeEventListener('pageshow', onPageShow);
  window.removeEventListener('online', onOnline);
}

/* ── Public lifecycle ────────────────────────────────────────────────────── */

/**
 * Start the reminder engine. Idempotent — calling it while already running is a
 * no-op, which is what lets both `applyTokenResponse()` and `restore()` call it
 * unconditionally.
 */
export function startEventReminders(): void {
  if (running) return;
  running = true;

  // The tenant+user storage key is only knowable once a token exists, which is
  // why hydration is a call rather than the store's initial state.
  try {
    useSettingsStore().hydrateEventReminderLead();
  } catch {
    /* Pinia not active yet — currentLead() falls back to the default */
  }

  startMetronome();
  attachListeners();

  // Runs alongside the first plan rather than before it: a slow (or missing)
  // preference endpoint must not delay arming reminders, and adopting a
  // different value simply reschedules.
  void adoptServerPreference();
  void refreshPlan(true);
  // Catch anything that came due while the app was closed, from the plan
  // already on disk — `missed()` silences whatever is too old to be useful.
  tick();
}

/**
 * Stop ticking and detach listeners. Idempotent.
 *
 * Deliberately does NOT clear storage or the device: a stop can be a plain
 * teardown, and a plan that survives it is exactly what lets a restored session
 * pick up where it left off. Sign-out calls {@link clearEventReminders} too.
 */
export function stopEventReminders(): void {
  running = false;
  stopMetronome();
  detachListeners();
  dueReminders.value = [];
}

/**
 * Forget this user's plan entirely — storage AND any armed device notification.
 *
 * MUST be called while the access token is still decodable: the storage key is
 * built from the tenant and the user id, so after `clearSession()` there is no
 * way left to name the key to remove. Hence its position in `signOutLocal()`,
 * immediately before the token is dropped.
 */
export function clearEventReminders(): void {
  dueReminders.value = [];
  lastRefreshAt = 0;
  replanQueued = false;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(storageKey());
    } catch {
      /* nothing to clear */
    }
  }
  // Leaving these armed would put an event title from an ended session on the
  // lock screen, possibly after the device changed hands.
  void localNotifications.cancelAllMine();
}

/**
 * Rebuild the plan from scratch, now.
 *
 * Called when the lead preference changes: every reminder key embeds the lead,
 * so the entire key set turns over and the old device notifications are
 * cancelled as "removed from the plan".
 */
export function rescheduleEventReminders(): void {
  lastRefreshAt = 0;
  void refreshPlan(true);
}

/**
 * Mark the plan stale so the next opportunity refreshes it.
 *
 * For changes that alter WHO is reminded rather than when — an RSVP, in
 * particular, flips `shouldRemind()` for that occurrence — where waiting up to
 * fifteen minutes would arm (or fail to arm) the wrong reminder.
 */
export function refreshEventRemindersSoon(): void {
  lastRefreshAt = 0;
  void refreshPlan(false);
}
